// Query params and URL hash fragments that may carry OAuth codes, invite tokens, PII, API keys, or
// S3/Cloudinary signing credentials. Lowercase names, matched case-insensitively.
// KEEP_IN_SYNC with `sensitiveUrlParams` in weavy/core/src/shared/logger/sanitize-log-value.ts.
export const SENSITIVE_PARAMS = new Set([
  'code',
  'state',
  'token',
  'email',
  'workspacename',
  'access_token',
  'id_token',
  'refresh_token',
  'password',
  'signature',
  'x-amz-credential',
  'x-amz-signature',
  'x-amz-security-token',
  '__cld_token__',
  'key',
]);

// Path segments that embed sensitive tokens, e.g. /v1/workspaces/members/invitation/<token>/validate.
export const SENSITIVE_PATH_REPLACEMENTS: Array<[RegExp, string]> = [[/(\/invitation\/)[^/?#]+/g, '$1[REDACTED]']];

export const REDACTED_VALUE = '[REDACTED]';

export function scrubParams(params: URLSearchParams): boolean {
  let changed = false;
  for (const key of [...params.keys()]) {
    if (SENSITIVE_PARAMS.has(key.toLowerCase())) {
      params.set(key, REDACTED_VALUE);
      changed = true;
    }
  }
  return changed;
}

export const REDACTED_RESOURCE = '[redacted-resource]';

/**
 * For resource URLs in RUM (resource events + the view LCP element) and the Logs network-error
 * pipeline: keep a first-party URL (our API or our own page origin, scrubbed for params) and reduce
 * every other URL to its origin, dropping the path and query. A generated-image or signed-asset path is
 * a replayable capability its endpoint serves without auth, and `scrubUrl` only strips query params, so
 * the path itself is the locator. Opaque origins (`data:` → `"null"`) and unparseable URLs collapse to a
 * constant. `firstPartyOrigins` must hold validated http(s) origins; the opaque `"null"` case is handled
 * before the membership test, so an empty list still fails safe to redaction.
 */
export function scrubResourceUrl(rawUrl: string | undefined, firstPartyOrigins: readonly string[]): string | undefined {
  if (!rawUrl) return rawUrl;
  let origin: string;
  try {
    origin = new URL(rawUrl).origin;
  } catch {
    return REDACTED_RESOURCE;
  }
  if (origin === 'null') return REDACTED_RESOURCE;
  if (firstPartyOrigins.includes(origin)) return scrubUrl(rawUrl);
  return `${origin}/${REDACTED_RESOURCE}`;
}

export function scrubUrl(rawUrl: string | undefined, baseUrl?: string): string | undefined {
  if (!rawUrl) return rawUrl;
  try {
    const base = baseUrl ?? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const url = new URL(rawUrl, base);
    let changed = scrubParams(url.searchParams);
    for (const [pattern, replacement] of SENSITIVE_PATH_REPLACEMENTS) {
      const scrubbedPath = url.pathname.replace(pattern, replacement);
      if (scrubbedPath !== url.pathname) {
        url.pathname = scrubbedPath;
        changed = true;
      }
    }
    if (url.hash.length > 1) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      if (scrubParams(hashParams)) {
        url.hash = `#${hashParams.toString()}`;
        changed = true;
      }
    }
    // Only re-serialize (which normalizes host/port/encoding) when something was redacted, so
    // benign URLs left inside error stacks keep their exact original form (and source-map frames).
    return changed ? url.toString() : rawUrl;
  } catch {
    return rawUrl;
  }
}

const EMBEDDED_URL_PATTERN = /https?:\/\/[^\s"'<>)\]]+/gi;

export function scrubText(text: string, scrubEmbeddedUrl: (url: string) => string | undefined = scrubUrl): string {
  return text.replace(EMBEDDED_URL_PATTERN, (match) => scrubEmbeddedUrl(match) ?? match);
}
